import React, { Component, ReactNode } from 'react';
import { View, Text } from 'react-native';
import { AlertTriangle, RotateCcw } from 'lucide-react-native';
import { reportError, getUserMessage } from '@/lib/errors';
import AnimatedPressable from '@/components/AnimatedPressable';

interface Props {
  children: ReactNode;
  /** Optional fallback component. Receives error + reset function. */
  fallback?: (props: { error: Error; resetError: () => void }) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global Error Boundary
 *
 * Catches unhandled React rendering errors, reports them via the centralized
 * error system, and shows a branded fallback screen with a retry button.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 *
 * Or with a custom fallback:
 *   <ErrorBoundary fallback={({ error, resetError }) => <MyFallback />}>
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    reportError(error, {
      context: 'ErrorBoundary',
      level: 'fatal',
      extra: { componentStack: info.componentStack },
    });
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback({ error: this.state.error, resetError: this.resetError });
      }

      return <DefaultFallback error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Default branded fallback screen
// ---------------------------------------------------------------------------

function DefaultFallback({ error, resetError }: { error: Error; resetError: () => void }) {
  return (
    <View
      style={{
        flex: 1,
        backgroundColor: '#005C5C',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 32,
      }}
    >
      {/* Icon */}
      <View
        style={{
          width: 72,
          height: 72,
          borderRadius: 36,
          backgroundColor: 'rgba(255,255,255,0.12)',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 24,
        }}
      >
        <AlertTriangle size={36} color="#FFFFFF" />
      </View>

      {/* Title */}
      <Text
        style={{
          color: '#FFFFFF',
          fontSize: 22,
          fontWeight: '700',
          textAlign: 'center',
          marginBottom: 8,
        }}
      >
        Something went wrong
      </Text>

      {/* Message */}
      <Text
        style={{
          color: 'rgba(255,255,255,0.7)',
          fontSize: 15,
          textAlign: 'center',
          lineHeight: 22,
          marginBottom: 32,
        }}
      >
        An unexpected error occurred. You can try again, and if the problem persists please contact support.
      </Text>

      {/* Dev-only error details */}
      {__DEV__ && (
        <View
          style={{
            backgroundColor: 'rgba(0,0,0,0.25)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 24,
            width: '100%',
          }}
        >
          <Text
            style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontFamily: 'monospace' }}
            numberOfLines={6}
          >
            {getUserMessage(error)}
          </Text>
        </View>
      )}

      {/* Retry button */}
      <AnimatedPressable
        onPress={resetError}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: '#FFFFFF',
          paddingHorizontal: 28,
          paddingVertical: 14,
          borderRadius: 14,
          gap: 8,
        }}
      >
        <RotateCcw size={18} color="#005C5C" />
        <Text style={{ color: '#005C5C', fontWeight: '600', fontSize: 16 }}>
          Try Again
        </Text>
      </AnimatedPressable>
    </View>
  );
}
